/**
 * Format phone number with a dash at specified position
 */
export function formatPhoneNumber(number: string, slicePlace = 3): string {
  // Remove all non-digit characters
  let sanitized = number.replace(/[^0-9]/g, '');

  // Apply the desired format: 123-1234564
  if (sanitized.length > slicePlace) {
    sanitized = sanitized.slice(0, slicePlace) + '-' + sanitized.slice(slicePlace);
  }

  return sanitized;
}

/**
 * Format phone number into parts (country code, area code, phone)
 */
export function formatPhoneNumberParts(phoneNumber: string): string[] {
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Define the lengths for country code, area code, and phone number
  const countryCodeLength = 4; // Example: 2 digits for country code
  const areaCodeLength = 3; // Example: 3 digits for area code

  // Extract the parts
  const countryCode = cleaned.slice(0, countryCodeLength);
  const areaCode = cleaned.slice(countryCodeLength, countryCodeLength + areaCodeLength);
  const phone = cleaned.slice(countryCodeLength + areaCodeLength);

  return [countryCode, areaCode, phone];
}

import parsePhoneNumber from 'libphonenumber-js';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

/**
 * Parse and format phone number (modify version 1 - for IR region)
 */
export function parsePhoneNumbersModify1(number: string): string {
  if (!number) return '';
  const isValidNumber = parsePhoneNumber(number, 'IR')?.isValid();
  if (!isValidNumber && !number.startsWith('+')) {
    number = number.startsWith('353') ? `+${number}` : `+353${number}`;
  }
  number = number.startsWith('+') || number.startsWith('00') ? number : `+${number}`;
  const phoneNumberParts = parsePhoneNumber(number, 'IR')?.formatInternational().split(' ');
  const containsZeroAfterPrefix = /^\+3530/.test(number);

  if (containsZeroAfterPrefix) {
    phoneNumberParts?.splice(1, 0, '0');
  }

  if (phoneNumberParts && phoneNumberParts.length >= 3) {
    const countryCode = phoneNumberParts[0];
    const areaCode = phoneNumberParts[1];
    const restOfNumber = phoneNumberParts.slice(2).join('-');
    return `${countryCode} ${areaCode} ${restOfNumber}`;
  }

  return number; // Return the original number if formatting fails
}

/**
 * Parse and format phone number (modify version - for IE region)
 */
export function parsePhoneNumbersModify(number: string): string {
  if (!number) return '';
  const isValidNumber = parsePhoneNumberFromString(number, 'IE')?.isValid();
  if (!isValidNumber) {
    return number;
  }
  number = number.startsWith('+') || number.startsWith('00') ? number : `+${number}`;
  const phoneNumberParts = parsePhoneNumberFromString(number, 'IE')?.formatInternational().split(' ');

  if (phoneNumberParts && phoneNumberParts.length >= 3) {
    const countryCode = phoneNumberParts[0];
    const areaCode = phoneNumberParts[1];
    const restOfNumber = phoneNumberParts.slice(2).join('-');
    return `${countryCode} ${areaCode} ${restOfNumber}`;
  }

  return number; // Return the original number if formatting fails
}

/**
 * Parse phone number globally (for IR region) and return parts
 */
export function parsePhoneNumberGlobal(number: string): string[] {
  const isValidNumber = parsePhoneNumber(number, 'IR')?.isValid();

  if (!isValidNumber && !number.startsWith('+')) {
    number = number.startsWith('353') ? `+${number}` : `+353${number}`;
  }
  number = number.startsWith('+') || number.startsWith('00') ? number : `+${number}`;
  const phoneNumberParts = parsePhoneNumber(number, 'IR')?.formatInternational().split(' ');
  const containsZeroAfterPrefix = /^\+3530/.test(number);

  if (phoneNumberParts && phoneNumberParts.length >= 3) {
    return [
      phoneNumberParts[0], // Country code
      phoneNumberParts[1], // Area code
      phoneNumberParts.slice(2).join('-'), // Rest of the number
    ];
  }
  return phoneNumberParts!;
}
